import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaPhone, FaClock, FaQuestionCircle, FaLifeRing } from 'react-icons/fa';

const Support = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowAlert(false);

    try {
      const emailData = {
        to: 'admin@mileageriot.com',
        from: formData.email,
        name: formData.name,
        subject: formData.subject,
        message: formData.message,
        timestamp: new Date().toISOString()
      };

      await new Promise(resolve => setTimeout(resolve, 2000));

      setSubmitStatus('success');
      setShowAlert(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      setTimeout(() => {
        setShowAlert(false);
      }, 5000);

    } catch (error) {
      console.error('Error sending support message:', error);
      setSubmitStatus('error');
      setShowAlert(true);

      setTimeout(() => {
        setShowAlert(false);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-page">
      <Container className="py-5 mt-3">
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="text-center mb-5">
              <div className="support-header-icon mb-4">
                <FaLifeRing className="support-main-icon" />
              </div>
              <h1 className="display-4 fw-bold text-white mb-3">
                {t('support.title')}
              </h1>
              <p className="lead text-light">
                {t('support.subtitle')}
              </p>
            </div>

            {/* Support Options Grid */}
            <Row className="mb-5">
              <Col md={4} className="mb-4">
                <Card className="support-option-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="support-option-icon mb-3">
                      <FaEnvelope />
                    </div>
                    <h5 className="text-white mb-2">
                      {t('support.contact.title')}
                    </h5>
                    <p className="text-light mb-3 small">
                      {t('support.contact.description')}
                    </p>
                    <a
                      href="mailto:admin@mileageriot.com"
                      className="btn btn-outline-primary btn-sm"
                    >
                      admin@mileageriot.com
                    </a>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4} className="mb-4">
                <Card className="support-option-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="support-option-icon mb-3">
                      <FaClock />
                    </div>
                    <h5 className="text-white mb-2">{t('support.responseTime')}</h5>
                    <p className="text-light mb-3 small">
                      {t('support.response_time')}
                    </p>
                    <span className="badge bg-primary">{t('support.responseHours')}</span>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4} className="mb-4">
                <Card className="support-option-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="support-option-icon mb-3">
                      <FaQuestionCircle />
                    </div>
                    <h5 className="text-white mb-2">{t('support.faq.title')}</h5>
                    <p className="text-light mb-3 small">
                      {t('support.faq.description')}
                    </p>
                    <a
                      href="https://discord.gg/nQSQmJbXN3"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                    >
                      {t('support.faq.discord')}
                    </a>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Alert for form submission */}
            {showAlert && (
              <Alert
                variant={submitStatus === 'success' ? 'success' : 'danger'}
                dismissible
                onClose={() => setShowAlert(false)}
                className="mb-4 support-alert"
              >
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    {submitStatus === 'success' ? (
                      <FaCheckCircle className="fs-4" />
                    ) : (
                      <FaExclamationCircle className="fs-4" />
                    )}
                  </div>
                  <div>
                    <Alert.Heading className="h5 mb-1">
                      {submitStatus === 'success'
                        ? t('support.form.success.title')
                        : t('support.form.error.title')
                      }
                    </Alert.Heading>
                    <p className="mb-0 small">
                      {submitStatus === 'success'
                        ? t('support.form.success.message')
                        : t('support.form.error.message')
                      }
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            {/* Support Form Card */}
            <Card className="support-form-card shadow-lg">
              <Card.Header className="support-form-header text-white">
                <div className="d-flex align-items-center">
                  <FaPaperPlane className="me-3 fs-4" />
                  <div>
                    <h3 className="mb-0">
                      {t('support.form.title')}
                    </h3>
                    <small className="opacity-75">{t('support.form.subtitle')}</small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4 support-form-body">
                <Form onSubmit={handleSubmit} className="support-form">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="support-form-label">
                          {t('support.form.name')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder={t('support.form.name_placeholder')}
                          className="support-form-control"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="support-form-label">
                          {t('support.form.email')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder={t('support.form.email_placeholder')}
                          className="support-form-control"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label className="support-form-label">
                      {t('support.form.subject')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder={t('support.form.subject_placeholder')}
                      className="support-form-control"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="support-form-label">
                      {t('support.form.message')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder={t('support.form.message_placeholder')}
                      className="support-form-control"
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      size="lg"
                      type="submit"
                      disabled={isSubmitting}
                      className="support-submit-btn py-3"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {t('support.form.sending')}
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="me-2" />
                          {t('support.form.submit')}
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Support Footer */}
            <div className="support-footer text-center mt-5">
              <div className="support-footer-content">
                <FaClock className="support-footer-icon mb-2" />
                <p className="text-light mb-0">
                  {t('support.response_time')}
                </p>
                <small className="text-muted">
                  {t('support.urgentContact')}
                </small>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Support;
